import React, {
    memo,
    useCallback,
    useEffect,
    useState,
} from 'react';

import ReactDOM from 'react-dom';
import {
    useDispatch,
    useSelector,
} from 'react-redux';

import dynamic from 'next/dynamic';

import _get from 'lodash/get';

// bonnet
import { useDevice } from '@bonnet/next/device';
import { ErrorBoundary } from '@bonnet/next/error-boundary';

import { useFeatures } from '@atc/features';

import { sendClick } from 'reaxl-analytics';
import {
    myWalletNonDealNotificationsClick,
    seeDetailsGlobalNavDealClick,
} from 'reaxl-analytics-handlers';

// analytics
import timingUtil from '@/utilities/timingUtil';

import useDataLayer from '@/analytics/useDataLayer';

// modules
import authDuck from '@/ducks/authDuck.js';
import dealsDuck from '@/ducks/dealsDuck.js';
import paymentsDuck from '@/ducks/paymentsDuck.js';
import recentSearchDuck from '@/ducks/recentSearchDuck.js';
import savedInventoryDuck from '@/ducks/savedInventoryDuck.js';
import userDuck from '@/ducks/userDuck.js';
import userPreferencesDuck from '@/ducks/userPreferencesDuck.js';

import useVdpNavigation from '@/hooks/useVdpNavigation';

import PersonalizationEngineModule from '@/modules/PersonalizationEngineModule';

// Utilities

import LazyComponent from '@/components/LazyComponent';

const AuthenticationHeader = dynamic(() => import('reaxl-organisms/es/AuthenticationHeader'), { ssr: false });

function HeaderContentContainer({
    listing = {},
    onLoggedInStateReady = () => { },
    saveListing,
    sendAlertClickHandler,
    showHeaderContent = true,
    showEmailSubscribe = false,
    onLogInSuccess = () => { },
    onSignOutClick = () => { },
    onEmailSubscribe = () => { },
    ...rest
}) {
    const [headerContentRef, changeHeaderContentRef] = useState(null);
    const dataLayer = useDataLayer();
    const dispatch = useDispatch();
    const device = useDevice();

    const isLg = _get(device, 'is.lg', false);

    const isLoggedIn = useSelector(authDuck.selectors.isLoggedIn);
    const peData = useSelector(PersonalizationEngineModule.duck.selectors.selectPersonalization);
    const dealNotifications = useSelector(dealsDuck.selectors.getDealNotifications);
    const dealDetails = useSelector(dealsDuck.selectors.getTransformedDealDetails);

    const tradeIn = useSelector(paymentsDuck.selectors.getTradeIn) || {};
    const { hasNewTradeIn = false } = tradeIn;

    const {
        authentication_header: [enableAuthenticationHeader],
        my_atc_shareable_link: [enableShareListings],
        my_wallet: [enableMyWallet, { show_popover: showWalletPopover }],
    } = useFeatures(['authentication_header', 'my_atc_shareable_link', 'my_wallet']);

    const isClientRendered = typeof window !== 'undefined';
    const popoverContainerEl = isClientRendered && document.body;

    const navigateToVdp = useVdpNavigation();

    useEffect(() => {
        if (isClientRendered && headerContentRef === null && enableAuthenticationHeader) {
            changeHeaderContentRef(document.getElementsByClassName('header-content'));
            timingUtil.markUserTime('header-rendered');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isClientRendered]);

    const handleQuickSaveItemClick = async (event, quickSaveListing) => {
        dispatch(userDuck.creators.hideQuickSaveList());
        await navigateToVdp({ listingId: quickSaveListing.id });
    };

    const handleQuickSaveItemToggle = useCallback((event, savedListing, wasSaved) => {
        saveListing({
            event,
            listing: savedListing,
            wasSaved,
            sendClick,
        });
    }, [saveListing]);

    const handleSaveInventoryUpdate = useCallback((data) => {
        sendAlertClickHandler({
            data,
            sendClick,
            peData,
            dataLayer,
            saveType: 'car',
        });
    }, [sendAlertClickHandler, peData, dataLayer]);

    const handleSaveSearchUpdate = useCallback((data) => {
        sendAlertClickHandler({
            data,
            sendClick,
            peData,
            dataLayer,
            saveType: 'search',
        });
    }, [sendAlertClickHandler, peData, dataLayer]);

    const handleClick = (e, data) => {
        sendClick('headerGeneralEventHandler', e, { ...data });
    };

    const handleShareListingsClick = useCallback(async (/* listings */) => {
        await dispatch(userPreferencesDuck.creators.setKeys({ showShareListingsModal: true }));
    }, [dispatch]);

    const handleWalletNotificationClick = useCallback((e, options = {}) => {
        if (options?.isNonDeal) {
            sendClick(myWalletNonDealNotificationsClick, e, options.data);
        } else {
            let location = '';
            // tradeIn only
            if (hasNewTradeIn && (!dealNotifications?.total)) {
                location = 'header-trade-in';
            } else if ((hasNewTradeIn && dealNotifications?.total) || (Object.keys(dealNotifications).length > 2)) {
                location = 'header-multiple-updates'; // ?
            } else if (!hasNewTradeIn && Object.keys(dealNotifications).length) {
                location = 'header-financing';
            }
            sendClick(seeDetailsGlobalNavDealClick, e, { location });
        }
        dispatch(paymentsDuck.creators.updateMyWallet({ displayModal: true, selectedPanelName: e.target.dataset.panel }));
    }, [dealNotifications, dispatch, hasNewTradeIn]);

    const walletProps = {
        dealDetails,
        dealNotifications,
        dealsDuck,
        enableMyWallet,
        enableWalletPopover: isLg && showWalletPopover,
        hasNewTradeIn,
        onWalletnotificationClick: handleWalletNotificationClick,
        paymentsDuck,
        portalTarget: popoverContainerEl,
    };

    const renderPersonalizationHeader = useCallback(() => (
        (
            <LazyComponent fallback={null}>
                <ErrorBoundary>
                    <AuthenticationHeader
                        onClick={handleClick}
                        onLoggedInStateReady={onLoggedInStateReady}
                        onLogInSuccess={onLogInSuccess}
                        onSignOutClick={onSignOutClick}
                        onShareListingsClick={enableShareListings && handleShareListingsClick}
                        onQuickSaveItemClick={handleQuickSaveItemClick}
                        onQuickSaveItemToggle={handleQuickSaveItemToggle}
                        onEmailSubscribe={onEmailSubscribe}
                        onSaveInventoryUpdate={handleSaveInventoryUpdate}
                        onSaveSearchUpdate={handleSaveSearchUpdate}
                        showEmailSubscribe={showEmailSubscribe}
                        userDuck={userDuck}
                        recentSearchDuck={recentSearchDuck}
                        savedInventoryDuck={savedInventoryDuck}
                        isLoggedIn={isLoggedIn}
                        showPsxNotificationsCount
                        {...walletProps}
                        {...rest}
                    />
                </ErrorBoundary>
            </LazyComponent>
        )
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ), [
        onLoggedInStateReady,
        onLogInSuccess,
        onSignOutClick,
        handleQuickSaveItemToggle,
        handleSaveInventoryUpdate,
        handleSaveSearchUpdate,
        onEmailSubscribe,
    ]);

    if (typeof window !== 'undefined' && headerContentRef && showHeaderContent && enableAuthenticationHeader) {
        return ReactDOM.createPortal(
            renderPersonalizationHeader(),
            headerContentRef[0]
        );
    }

    return null;

}

export default memo(HeaderContentContainer);
